"use client";

import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

interface PremiumMultiSelectProps {
  label: string;
  options: string[];
  selected: string[];
  isExclude: boolean;
  onExcludeToggle: (val: boolean) => void;
  plural?: string;
  onChange: (vals: string[]) => void;
  searchValue?: string;
  onSearchChange?: (val: string) => void;
}

export const PremiumMultiSelect = ({
  label,
  options,
  selected,
  isExclude,
  onExcludeToggle,
  plural,
  onChange,
  searchValue,
  onSearchChange,
}: PremiumMultiSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filterSearchInternal, setFilterSearchInternal] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  const filterSearch = onSearchChange && searchValue !== undefined ? searchValue : filterSearchInternal;
  const setFilterSearch = (val: string) => {
    if (onSearchChange) {
      onSearchChange(val);
    } else {
      setFilterSearchInternal(val);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleOption = (opt: string) => {
    if (selected.includes(opt)) {
      onChange(selected.filter((s) => s !== opt));
    } else {
      onChange([...selected, opt]);
    }
  };

  const normalizeString = (str: string) =>
    str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

  const filteredOptions = (options || []).filter(opt =>
    normalizeString(opt === "" ? "NOT ASSIGNED" : opt).includes(normalizeString(filterSearch))
  );

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "w-full border rounded px-2 py-1 text-[8px] font-normal uppercase tracking-widest text-left flex items-center justify-between transition-colors min-h-[28px]",
          isExclude && selected.length > 0
            ? "bg-[#AE232D] border-[#AE232D] text-white"
            : "bg-[#003d45] border-white/20 text-white hover:border-white/40"
        )}
      >
        <span className="truncate pr-1">
          {selected.length === 0
            ? (searchValue ? `"${searchValue}"` : "ALL")
            : isExclude
              ? `!(${selected.length})`
              : `(${selected.length})`}
        </span>
        <ChevronRight className={cn("w-3 h-3 flex-shrink-0 transition-transform", isOpen && "rotate-90")} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute top-full left-0 mt-2 bg-[#002d33] border border-white/10 rounded-xl shadow-2xl z-[100] min-w-[220px] max-h-80 flex flex-col overflow-hidden"
          >
            <div className="p-3 border-b border-white/10 space-y-3 bg-[#00242a]">
              <div className="flex items-center justify-between">
                <span className="text-[8px] font-normal text-white/40 uppercase tracking-widest">Exclude Mode</span>
                <button
                  onClick={() => onExcludeToggle(!isExclude)}
                  className={cn(
                    "w-8 h-4 rounded-full relative transition-colors",
                    isExclude ? "bg-[#AE232D]" : "bg-white/20"
                  )}
                >
                  <div className={cn(
                    "absolute top-0.5 w-3 h-3 bg-white rounded-full transition-all",
                    isExclude ? "left-4.5" : "left-0.5"
                  )} />
                </button>
              </div>

              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-white/30" />
                <input
                  type="text"
                  placeholder={`SEARCH ${label}...`}
                  value={filterSearch}
                  onChange={(e) => setFilterSearch(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-lg py-1.5 pl-7 pr-3 text-[10px] font-normal text-white placeholder:text-white/20 focus:outline-none focus:border-[#AE232D]/50 uppercase tracking-wider"
                  autoFocus
                />
              </div>
            </div>

            <div className="overflow-auto custom-scrollbar p-2 max-h-60">
              <div className="flex flex-col gap-1">
                <button
                  onClick={() => { onChange([]); setIsOpen(false); }}
                  className={cn(
                    "px-3 py-2 rounded-lg text-left text-[9px] font-normal uppercase tracking-wider transition-colors",
                    selected.length === 0 ? "bg-[#004D57] text-white" : "text-white/50 hover:bg-white/5"
                  )}
                >
                  All {plural || label + 's'}
                </button>
                {filteredOptions.length === 0 ? (
                  searchValue !== undefined ? null : (
                    <div className="px-3 py-4 text-center">
                      <span className="text-[9px] font-normal text-white/20 uppercase tracking-widest">No matches</span>
                    </div>
                  )
                ) : (
                  <div className="flex flex-col gap-1">
                    {filteredOptions.map((opt) => (
                      <button
                        key={opt}
                        onClick={() => toggleOption(opt)}
                        className={cn(
                          "px-3 py-2 rounded-lg text-left text-[9px] font-normal uppercase tracking-wider transition-colors flex items-center justify-between",
                          selected.includes(opt) ? "bg-[#AE232D]/20 text-white" : "text-white/70 hover:bg-white/5"
                        )}
                      >
                        <span className="truncate pr-2">{opt === "" ? "NOT ASSIGNED" : opt}</span>
                        {selected.includes(opt) && <div className="w-1.5 h-1.5 rounded-full bg-[#AE232D]" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
